import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Loader2,
  Save,
  Sparkles,
  Trash2,
  Linkedin,
  Instagram,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, clearAdminToken, getAdminToken } from "@/lib/api";

const PERSONAS = [
  { v: "salaried", l: "Salaried Professionals" },
  { v: "self_employed", l: "Self-Employed / Professionals" },
  { v: "business_owner", l: "Business Owners / MSME" },
  { v: "property_investor", l: "Property Investors" },
  { v: "general", l: "General audience" },
];
const TONES = [
  { v: "educational", l: "Educational" },
  { v: "conversational", l: "Conversational" },
  { v: "aspirational", l: "Aspirational" },
  { v: "urgent", l: "Urgent / Newsy" },
];

export default function AdminContent() {
  const navigate = useNavigate();
  const [persona, setPersona] = useState("business_owner");
  const [tone, setTone] = useState("conversational");
  const [topic, setTopic] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    loadLibrary();
  }, [navigate]);

  const loadLibrary = async () => {
    try {
      const r = await api.get("/content/library");
      setLibrary(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
      }
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (topic.trim().length < 4) return toast.error("Add a topic (at least a few words).");
    setLoading(true);
    setDraft(null);
    try {
      const r = await api.post("/content/generate", { persona, topic, tone });
      setDraft(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post("/content/save", {
        persona: draft.persona,
        topic: draft.topic,
        tone: draft.tone,
        linkedin: draft.linkedin,
        instagram: draft.instagram,
        instagram_hashtags: draft.instagram_hashtags || [],
        whatsapp: draft.whatsapp,
      });
      toast.success("Saved to library");
      loadLibrary();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/content/${id}`);
      setLibrary((l) => l.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed")
    );
  };

  return (
    <div data-testid="admin-content-page" className="container-x py-10 md:py-14">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="eyebrow">Admin · Content Studio</span>
          <h1 className="mt-3 h-display text-3xl sm:text-4xl">
            Draft LinkedIn, Instagram &amp; WhatsApp posts.
          </h1>
          <p className="mt-3 text-brand-sage max-w-2xl">
            Pick a persona, drop a topic, hit Generate. Claude Sonnet 4.5 returns three platform-tuned drafts you can copy with one click.
          </p>
        </div>
        <Link to="/admin/dashboard" className="btn-ghost">
          <ArrowLeft className="h-4 w-4" /> Back to leads
        </Link>
      </div>

      <form onSubmit={generate} className="mt-8 card-soft p-6 md:p-7" data-testid="admin-content-form">
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-4">
            <Label className="text-sm font-medium text-brand-forest">Persona</Label>
            <Select value={persona} onValueChange={setPersona}>
              <SelectTrigger data-testid="content-persona" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERSONAS.map((p) => (
                  <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-sm font-medium text-brand-forest">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="content-tone" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4 flex items-end">
            <button
              type="submit"
              disabled={loading}
              data-testid="content-generate-btn"
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>) : (<><Sparkles className="h-4 w-4" /> Generate</>)}
            </button>
          </div>
          <div className="md:col-span-12">
            <Label className="text-sm font-medium text-brand-forest">Topic / angle</Label>
            <Textarea
              data-testid="content-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder='e.g. "Why MSMEs should refinance high-interest unsecured loans now that RBI has cut repo rates"'
              className="mt-1.5 min-h-[80px]"
            />
          </div>
        </div>
      </form>

      {draft && (
        <section className="mt-8 grid lg:grid-cols-3 gap-5" data-testid="content-draft-result">
          <DraftCard
            icon={<Linkedin className="h-5 w-5" />}
            label="LinkedIn"
            color="bg-[#0A66C2]"
            text={draft.linkedin}
            onCopy={() => copyText(draft.linkedin, "LinkedIn post")}
            testid="draft-linkedin"
          />
          <DraftCard
            icon={<Instagram className="h-5 w-5" />}
            label="Instagram"
            color="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]"
            text={draft.instagram}
            extra={
              draft.instagram_hashtags?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {draft.instagram_hashtags.map((h, i) => (
                    <span key={i} className="text-xs text-brand-terracotta font-medium bg-brand-terracotta/10 rounded-full px-2 py-0.5">
                      {h}
                    </span>
                  ))}
                </div>
              ) : null
            }
            onCopy={() => {
              const tags = (draft.instagram_hashtags || []).join(" ");
              const full = tags ? `${draft.instagram}\n\n${tags}` : draft.instagram;
              copyText(full, "Instagram caption + hashtags");
            }}
            testid="draft-instagram"
          />
          <DraftCard
            icon={<MessageCircle className="h-5 w-5" />}
            label="WhatsApp"
            color="bg-[#25D366]"
            text={draft.whatsapp}
            onCopy={() => copyText(draft.whatsapp, "WhatsApp message")}
            testid="draft-whatsapp"
          />
          <div className="lg:col-span-3 flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              data-testid="content-save-btn"
              className="btn-secondary disabled:opacity-60"
            >
              {saving ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>) : (<><Save className="h-4 w-4" /> Save to library</>)}
            </button>
          </div>
        </section>
      )}

      <section className="mt-14">
        <div className="flex items-center justify-between">
          <h2 className="font-outfit font-bold text-2xl text-brand-forest">Library</h2>
          <span className="text-sm text-brand-sage">{library.length} saved</span>
        </div>
        {library.length === 0 ? (
          <div data-testid="content-library-empty" className="mt-6 card-soft p-10 text-center text-brand-sage">
            Saved drafts will appear here. Generate one above and hit "Save to library".
          </div>
        ) : (
          <ul className="mt-6 grid lg:grid-cols-2 gap-5">
            {library.map((d) => (
              <li key={d.id} data-testid={`library-item-${d.id}`} className="card-soft p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Badge className="bg-brand-sand/60 text-brand-forest border border-brand-sand uppercase text-[10px] tracking-[0.14em] font-semibold">
                      {d.persona.replace(/_/g, " ")}
                    </Badge>
                    <h3 className="mt-2 font-outfit font-bold text-base text-brand-forest line-clamp-2">{d.topic}</h3>
                    <div className="mt-1 text-xs text-brand-sage">{new Date(d.created_at).toLocaleString()} · {d.tone}</div>
                  </div>
                  <button
                    onClick={() => deleteDraft(d.id)}
                    aria-label="Delete"
                    className="text-brand-sage hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 grid sm:grid-cols-3 gap-2">
                  <button onClick={() => copyText(d.linkedin, "LinkedIn")} className="text-xs font-semibold rounded-full px-3 py-1.5 bg-brand-forest text-brand-cream hover:bg-[#0a1d17] transition-colors">
                    Copy LinkedIn
                  </button>
                  <button onClick={() => copyText(`${d.instagram}\n\n${(d.instagram_hashtags || []).join(" ")}`, "Instagram")} className="text-xs font-semibold rounded-full px-3 py-1.5 bg-brand-terracotta text-white hover:bg-[#b25535] transition-colors">
                    Copy Instagram
                  </button>
                  <button onClick={() => copyText(d.whatsapp, "WhatsApp")} className="text-xs font-semibold rounded-full px-3 py-1.5 bg-[#25D366] text-white hover:bg-[#1ea854] transition-colors">
                    Copy WhatsApp
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function DraftCard({ icon, label, color, text, onCopy, testid, extra }) {
  return (
    <div data-testid={testid} className="card-soft p-6 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className={`grid h-9 w-9 place-items-center rounded-xl text-white ${color}`}>{icon}</span>
          <span className="font-outfit font-bold text-base text-brand-forest">{label}</span>
        </div>
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-forest text-brand-cream px-3 py-1.5 text-xs font-semibold hover:bg-[#0a1d17] transition-colors"
          data-testid={`${testid}-copy`}
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
      <p className="mt-4 text-sm text-brand-forest/85 leading-relaxed whitespace-pre-line flex-1">
        {text}
      </p>
      {extra}
    </div>
  );
}
